import type { GalleryItem, InscriptionAttributes, TraitValue } from "./types";

export const INSCRIPTION_ID_PATTERN = /^[0-9a-f]{64}i\d+$/i;

export const GALLERY_INPUT_EXAMPLE = `{
  "${"a".repeat(64)}i0": {
    "name": "Piece #1",
    "traits": { "Background": "Blue", "Edition": 1 }
  },
  "${"b".repeat(64)}i0": {
    "name": "Piece #2",
    "traits": { "Background": "Red", "Edition": 2 }
  }
}`;

export interface ParsedGalleryInput {
  items: GalleryItem[];
  jsonMode: boolean;
  invalidCount: number;
  duplicateCount: number;
  withMetadata: number;
  error?: string;
}

function record(value: unknown): Record<string, unknown> | null {
  return typeof value === "object" && value !== null && !Array.isArray(value)
    ? value as Record<string, unknown>
    : null;
}

function text(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value).trim() : "";
}

function traitValue(value: unknown): TraitValue | undefined {
  if (value === null || typeof value === "string" || typeof value === "boolean") return value;
  return typeof value === "number" && Number.isSafeInteger(value) ? value : undefined;
}

function traits(input: unknown): Record<string, TraitValue> | undefined {
  const result: Record<string, TraitValue> = {};
  if (Array.isArray(input)) {
    for (const candidate of input) {
      const item = record(candidate);
      if (!item) continue;
      const name = text(item.trait_type ?? item.traitType ?? item.type ?? item.key ?? item.name);
      const normalized = traitValue(item.value);
      if (name && normalized !== undefined) result[name] = normalized;
    }
  } else {
    for (const [name, value] of Object.entries(record(input) ?? {})) {
      const normalized = traitValue(value);
      if (name.trim() && normalized !== undefined) result[name.trim()] = normalized;
    }
  }
  return Object.keys(result).length ? result : undefined;
}

const ITEM_DETAIL_FIELDS = new Set([
  "id", "inscriptionId", "inscription_id", "iid",
  "name", "title", "meta", "metadata", "traits", "attributes", "properties",
]);

function looseTraits(input: Record<string, unknown>): Record<string, TraitValue> {
  const result: Record<string, TraitValue> = {};
  for (const [name, value] of Object.entries(input)) {
    if (ITEM_DETAIL_FIELDS.has(name)) continue;
    const normalized = traitValue(value);
    if (name.trim() && normalized !== undefined) result[name.trim()] = normalized;
  }
  return result;
}

function itemAttributes(value: unknown): InscriptionAttributes | undefined {
  const base = record(value);
  if (!base) return undefined;
  let title: string | undefined;
  const normalizedTraits: Record<string, TraitValue> = {};
  for (const candidate of [base, record(base.meta), record(base.metadata)].filter((item): item is Record<string, unknown> => item !== null)) {
    if (!title) {
      const nextTitle = text(candidate.name ?? candidate.title);
      if (nextTitle) title = nextTitle;
    }
    Object.assign(normalizedTraits, traits(candidate.traits ?? candidate.attributes ?? candidate.properties), looseTraits(candidate));
  }
  return title || Object.keys(normalizedTraits).length ? {
    title,
    traits: Object.keys(normalizedTraits).length ? normalizedTraits : undefined,
  } : undefined;
}

function candidateId(key: string, value: unknown): string {
  if (INSCRIPTION_ID_PATTERN.test(key.trim())) return key.trim();
  const item = record(value);
  return text(item?.id ?? item?.inscriptionId ?? item?.inscription_id ?? item?.iid);
}

function jsonEntries(value: unknown): Array<[string, unknown]> {
  if (Array.isArray(value)) return value.map((item) => ["", item]);
  const root = record(value);
  if (!root) return [];
  if (Array.isArray(root.items)) return root.items.map((item) => ["", item]);
  return Object.entries(root);
}

export function parseGalleryInput(raw: string): ParsedGalleryInput {
  const input = raw.trim();
  if (!input) return { items: [], jsonMode: false, invalidCount: 0, duplicateCount: 0, withMetadata: 0 };

  const jsonMode = input.startsWith("{") || input.startsWith("[");
  if (!jsonMode) {
    const values = input.split(/[\s,]+/).map((value) => value.trim()).filter(Boolean);
    const seen = new Set<string>();
    const items: GalleryItem[] = [];
    let invalidCount = 0;
    let duplicateCount = 0;
    for (const value of values) {
      if (!INSCRIPTION_ID_PATTERN.test(value)) {
        invalidCount += 1;
      } else if (seen.has(value.toLowerCase())) {
        duplicateCount += 1;
      } else {
        seen.add(value.toLowerCase());
        items.push({ inscriptionId: value });
      }
    }
    return { items, jsonMode: false, invalidCount, duplicateCount, withMetadata: 0 };
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(input) as unknown;
  } catch {
    return { items: [], jsonMode: true, invalidCount: 0, duplicateCount: 0, withMetadata: 0, error: "The gallery JSON is not valid." };
  }

  const seen = new Set<string>();
  const items: GalleryItem[] = [];
  let invalidCount = 0;
  let duplicateCount = 0;
  let withMetadata = 0;
  for (const [key, value] of jsonEntries(parsed)) {
    const id = candidateId(key, value);
    if (!INSCRIPTION_ID_PATTERN.test(id)) {
      invalidCount += 1;
      continue;
    }
    if (seen.has(id.toLowerCase())) {
      duplicateCount += 1;
      continue;
    }
    seen.add(id.toLowerCase());
    const attributes = itemAttributes(value);
    if (attributes) withMetadata += 1;
    items.push({ inscriptionId: id, attributes });
  }

  return {
    items,
    jsonMode: true,
    invalidCount,
    duplicateCount,
    withMetadata,
    error: items.length ? undefined : "No valid inscription IDs were found in the gallery JSON.",
  };
}
